<script setup>
import {computed, ref} from 'vue'
import {useStore} from 'vuex'
import {useRouter, useRoute} from 'vue-router'
import {ChevronRight, Folder, FolderPlus, Settings, Pencil, Eye, Folders} from 'lucide-vue-next'
import {bbox} from '@turf/turf'

import {Button} from '@/components/ui/button'

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])

const router = useRouter()
const route = useRoute()
const emit = defineEmits(['select'])
const props = defineProps({
  project: Object,
  openedProjectIds: Object
})

const routeProjectId = computed(() => parseInt(route.params.project_id))

const isSelected = computed(() => routeProjectId.value === props.project.project_id)
const isOpened = computed(() => !!props.openedProjectIds?.[props.project.project_id])
const isHovered = ref(false)

const select = (project, level = 0) => {
  emit('select', project, level)
}

const selectProject = () => {
  router.push({ name: 'ProjectHome', params: { org_id: props.project.organization_id, project_id: props.project.project_id } })
}

  const openProject = (project) => {
    router.push({ name: 'ProjectHome', params: { org_id: project.organization_id, project_id: project.project_id } })
  }

const createSubProject = () => {
  router.push({ name: 'ProjectCreate', params: { org_id: props.project.organization_id, project_id: props.project.project_id }})
}

const editProject = () => {
  router.push({ name: 'ProjectEdit', params: { org_id: props.project.organization_id, project_id: props.project.project_id } })
}

const zoomOnBoundary = async () => {
  const boundary = await store.dispatch('geo/fetchProjectBoundary', props.project.project_id)
  if (!boundary) return
  map.value.fitBounds(bbox(boundary))
  openProject(props.project)
}

const goToMembers = () => {
  router.push({ name: 'ProjectMembers', params: { org_id: props.project.organization_id, project_id: props.project.project_id } })
}

const isoToEmoji = (isoCode) => {
  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

const createProject = () => {
  router.push({ name: 'ProjectCreate', params: { org_id: props.project.organization_id, project_id: props.project.project_id }})
}
</script>

<template>
  <div v-if="project?.project_id" class="flex items-center px-0.5">
    <div
      class="flex items-center flex-1 w-full rounded-lg p-0.5"
      :class="{'bg-secondary': isHovered && !isSelected, 'bg-primary text-white': isSelected}"
      :value="project"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <Button
        variant="ghostDark"
        class="w-7 h-7 rounded-full p-0 text-gray-400 hover:text-black project-expand-toggle"
        :class="{'opacity-0': !project.children?.length, 'rotate-90 text-black': isOpened, 'text-white': isSelected}"
        :id="`project-expand-${project.project_id}`"
        :aria-label="isOpened ? `Collapse ${project.project_name} sub-projects` : `Expand ${project.project_name} sub-projects`"
        :aria-expanded="isOpened"
        :aria-controls="`project-children-${project.project_id}`"
        :title="isOpened ? `Collapse ${project.project_name} sub-projects` : `Expand ${project.project_name} sub-projects`"
        type="button"
        :disabled="!project.children?.length"
        @click.stop
        @click="select(project)"
      >
        <ChevronRight class="w-7 h-5" :aria-hidden="true" />
      </Button>
      <div class="flex items-center justify-between w-full min-w-[200px]">
        <Button
          variant="ghost"
          class="flex items-center h-full p-0.5 cursor-pointer min-w-0 flex-1 justify-start hover:bg-transparent"
          :class="{'truncate': isHovered}"
          :id="`project-name-${project.project_id}`"
          :aria-label="`Open ${project.project_name} project`"
          :title="`Open ${project.project_name} project`"
          type="button"
          @click.stop="openProject(project)"
        >
          <div class="w-4.5 h-4.5 mr-2 flex items-center justify-center">
            <span v-if="project.place_details">
              {{ isoToEmoji(project.place_details.country_code) }}
            </span>
            <Folder v-if="!project.children?.length" class="w-4.5 h-4.5 text-gray-700" :class="{'text-white': isSelected}" />
            <Folders v-else-if="!project.place_details" class="w-4.5 h-4.5 text-gray-700" :class="{'text-white': isSelected}" />
          </div>
          <span class="truncate" :title="project.project_name">
            {{ project.project_name }}
          </span>
        </Button>

        <div class="flex items-center justify-end project-actions" @click.stop>
          <Button
            variant="ghostDark"
            class="w-7 h-7 rounded-full p-0 text-gray-500 hover:text-black project-zoom-action"
            :class="{'text-white': isSelected}"
            :id="`project-zoom-${project.project_id}`"
            :aria-label="`View ${project.project_name} on map`"
            :title="`View ${project.project_name} on map`"
            type="button"
            @click="zoomOnBoundary"
          >
            <Eye class="w-4 h-4" :aria-hidden="true" />
          </Button>
          <Button
            variant="ghostDark"
            class="w-7 h-7 rounded-full p-0 text-gray-500 hover:text-black project-create-action"
            :class="{'text-white': isSelected}"
            :id="`project-create-${project.project_id}`"
            :aria-label="`Create sub-project under ${project.project_name}`"
            :title="`Create sub-project under ${project.project_name}`"
            type="button"
            @click="createProject"
          >
            <FolderPlus class="w-4 h-4" :aria-hidden="true" />
          </Button>
          <Button
            variant="ghostDark"
            class="w-7 h-7 rounded-full p-0 text-gray-500 hover:text-black project-edit-action"
            :class="{'text-white': isSelected}"
            :id="`project-edit-${project.project_id}`"
            :data-testid="`edit-project-${project.project_id}`"
            :aria-label="`Edit ${project.project_name}`"
            :title="`Edit ${project.project_name}`"
            type="button"
            @click="editProject"
          >
            <Pencil class="w-4 h-4" :aria-hidden="true" />
          </Button>
          <Button
            variant="ghostDark"
            class="w-7 h-7 rounded-full p-0 text-gray-500 hover:text-black project-members-action"
            :class="{'text-white': isSelected}"
            :id="`project-members-${project.project_id}`"
            :aria-label="`Manage ${project.project_name} members`"
            :title="`Manage ${project.project_name} members`"
            type="button"
            @click="goToMembers"
          >
            <Settings class="w-4 h-4" :aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  </div>

  <div
    class="pl-5 pr-1"
    v-if="isOpened"
    :id="`project-children-${project.project_id}`"
    role="group"
    :aria-label="`Sub-projects of ${project.project_name}`"
  >
    <ProjectListItem
      v-for="child in project.children"
      :key="child.project_id"
      :project="child"
      :opened-project-ids="openedProjectIds"
      @select="(project, level) => select(project, level + 1)"
    />
  </div>
</template>
